import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const EMPTY_FORM = {
  option_group_id: "",
  restaurant_id: "",
  group_name: "",
  description: "",
  status: "ACTIVE",
};

const fieldClass =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export default function BulkOptionGroups() {
  const {
    optionGroupsLoading,
    optionGroupSaving,
    optionGroupDeletingId,
    getOptionGroups,
    insertOptionGroup,
    updateOptionGroup,
    deleteOptionGroup,
  } = useBulkOptionGroups();

  const { getRestaurants } = useBulkCatalogue();

  const [rows, setRows] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    const groupRows = await getOptionGroups("");
    setRows(Array.isArray(groupRows) ? groupRows : []);
  };
  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const r = await getRestaurants("");
    const next = Array.isArray(r) ? r : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setForm({
      ...EMPTY_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
    setModalOpen(true);
  };

  const openEdit = async (row) => {
    await ensureRestaurants();
    setForm({
      option_group_id: String(row?.option_group_id ?? row?.id ?? ""),
      restaurant_id: String(row?.restaurant_id ?? ""),
      group_name: String(row?.group_name ?? ""),
      description: String(row?.description ?? ""),
      status: String(row?.status ?? "ACTIVE"),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (optionGroupSaving) return;
    setModalOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const saved = form.option_group_id
      ? await updateOptionGroup(form)
      : await insertOptionGroup(form);

    if (saved) {
      closeModal();
      await load();
    }
  };

  const handleDelete = async (row) => {
    const id = row?.option_group_id ?? row?.id;
    if (!id) return;

    const deleted = await deleteOptionGroup(id);
    if (deleted) await load();
  };

  const restaurantName = (id) =>
    restaurants.find(
      (restaurant) =>
        String(restaurant?.restaurant_id ?? restaurant?.id ?? "") ===
        String(id ?? ""),
    )?.restaurant_name || `Restaurant #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Option Groups
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage restaurant option groups.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={optionGroupsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={optionGroupsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>

          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <Plus size={13} />
            Add Option Group
          </button>
        </div>
      </div>

      {optionGroupsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          No option groups found.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {rows.map((row, index) => {
            const id = row?.option_group_id ?? row?.id ?? index;
            const deleting = optionGroupDeletingId === String(id);

            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {row?.group_name || "Unnamed Option Group"}
                    </p>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-600">
                      {row?.status || "—"}
                    </span>
                  </div>

                  <p className="mt-1 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(row?.restaurant_id)}
                  </p>

                  {row?.description ? (
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                      {row.description}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>

                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => handleDelete(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 disabled:opacity-50"
                  title="Delete"
                >
                  {deleting ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {form.option_group_id
                    ? "Edit Option Group"
                    : "Add Option Group"}
                </h3>
                <p className="text-xs text-slate-500">
                  Restaurant option group details
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={optionGroupSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>
                  <select
                    name="restaurant_id"
                    value={form.restaurant_id}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className={fieldClass}
                    required
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map((restaurant) => {
                      const id =
                        restaurant?.restaurant_id ?? restaurant?.id ?? "";
                      return (
                        <option key={id} value={id}>
                          {restaurant?.restaurant_name || `Restaurant #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Group Name
                  </span>
                  <input
                    name="group_name"
                    value={form.group_name}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className={fieldClass}
                    required
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className={fieldClass}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className="min-h-24 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-emerald-400"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={optionGroupSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={
                    optionGroupSaving ||
                    !form.restaurant_id ||
                    !form.group_name.trim()
                  }
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {optionGroupSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.option_group_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}

                  {optionGroupSaving
                    ? "Saving..."
                    : form.option_group_id
                      ? "Update Option Group"
                      : "Add Option Group"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
